import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

export interface RebirthConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 지금 환생 시 획득 예상 포인트 (기본 + 포인트보너스 강화) — RebirthTabData.gainedIfRebirth와 동일 식. */
  readonly gainedPoints: number;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const PANEL_W = 300;
const PANEL_H = 220;

/**
 * 환생 확인 팝업 — GridUnlockConfirmPopup 패턴(ModalLayer + NineSlicePanel + Yes/No).
 * 환생은 비가역 진행 리셋(골드·스탯 강화·챕터 진행도·보유 모듈 초기화)이라 오클릭 방지 확인을 거친다.
 * 본문에 초기화 항목 안내 + 획득 예상 환생 포인트를 표시하고, Yes(환생)에서만 onConfirm(연출+performRebirth)을 호출한다.
 * 실제 초기화는 셸(rebirthBoot → ProgressionCommands.performRebirth)이 onConfirm에서 수행 — 본 팝업은 확인 UI만.
 */
export class RebirthConfirmPopup extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: RebirthConfirmPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - PANEL_H / 2;
    // dim 클릭 닫기 — 패널 영역 제외(패널 내부 클릭은 안 닫힘).
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, PANEL_H);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 24, t("rebirth.confirm.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 18, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());

    // 초기화 항목 안내 — 비가역 리셋 대상(골드·스탯 강화·챕터·보유 모듈)을 본문에 명시.
    const body = scene.add
      .text(cx, top + 64, t("rebirth.confirm.body"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
        align: "center",
        lineSpacing: 5,
      })
      .setOrigin(0.5, 0)
      .setWordWrapWidth(PANEL_W - 44);

    // 획득 예상 환생 포인트 — 환생의 보상(강조색).
    const gain = scene.add
      .text(cx, top + PANEL_H - 66, t("rebirth.confirm.gain", { gained: opts.gainedPoints }), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    this.contentContainer.add([title, closeBtn, body, gain]);

    // Yes(환생) — onConfirm에서만 연출+performRebirth. No(취소) — 닫기만.
    const yesBtn = new NineSliceButton(scene, cx - 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("rebirth.confirm.yes"),
      widthOverride: 108,
    });
    yesBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onConfirm());
    const noBtn = new NineSliceButton(scene, cx + 62, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("rebirth.confirm.no"),
      widthOverride: 108,
    });
    noBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([yesBtn, noBtn]);
  }
}
